import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { Slider } from "@/components/ui/slider";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import { PuzzleSetup } from "@/components/PuzzleSetup";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Wand2, ImageIcon, Layers, Feather, BookText, Drama, Baby, Puzzle, Grid3x3, Route, Palette, Search, AlignLeft, AlignRight, User, NotebookPen, ClipboardList } from "lucide-react";

const TONES = ["Academic", "Conversational", "Narrative", "Journalistic", "Inspirational", "Technical"];
const FICTION_TONES = ["Literary", "Thriller", "Fantasy", "Sci-Fi", "Romance", "Mystery", "Adventure"];
const CHILDREN_TONES = ["Ages 3-5", "Ages 5-8", "Ages 8-10", "Rhyming", "Bedtime"];

const SAMPLES = [
  "The secret life of octopuses",
  "A beginner's guide to Stoic philosophy",
  "How the Roman Empire really fell",
  "The future of fusion energy",
];
const FICTION_SAMPLES = [
  "A detective who can taste lies",
  "The last lighthouse keeper on Mars",
  "Two rival chefs fall in love in Paris",
  "A city where dreams are currency",
];
const CHILDREN_SAMPLES = [
  "A little girl with red curls who plans a picnic on the moon",
  "Milo, a boy with brown hair and blue eyes, befriends a shy dragon",
  "A brave kitten who's scared of the rain",
  "Two best friends build a rocket from cardboard boxes",
];

export const CreatorForm = ({ onGenerate, busy, admin = false, entitled = false, seriesList = [], initialSeriesId = "", seed = null, onCreateSeries, onBuySeries, onManageSeries }) => {
  const [topic, setTopic] = useState("");
  const [pages, setPages] = useState(60);
  const [mode, setMode] = useState("nonfiction");
  const [nfFormat, setNfFormat] = useState("reference");
  const [engine, setEngine] = useState("classic");
  const [tone, setTone] = useState("Conversational");
  const [images, setImages] = useState(true);
  const [showAdv, setShowAdv] = useState(false);
  const [structure, setStructure] = useState("Auto");
  const [pov, setPov] = useState("Auto");
  const [audience, setAudience] = useState("Auto");
  const [bible, setBible] = useState("");
  const [chapterWords, setChapterWords] = useState(0);
  const [seriesId, setSeriesId] = useState("");
  const [difficulty, setDifficulty] = useState("medium");
  const [author, setAuthor] = useState("");
  const [authorBio, setAuthorBio] = useState("");
  const [direction, setDirection] = useState("ltr");
  const [journalStyle, setJournalStyle] = useState("guided");
  const [workbookStyle, setWorkbookStyle] = useState("educational");
  const [lineStyle, setLineStyle] = useState("ruled");
  const [proOpen, setProOpen] = useState(false);
  const [persona, setPersona] = useState({ name: "", background: "", why: "", stories: "", voice: "", freeform: "" });
  const [userStories, setUserStories] = useState("");
  const [personaAuto, setPersonaAuto] = useState(false);
  const [toneCal, setToneCal] = useState({ cinematic: 50, emotional: 50, founder: 50, direct: 50 });
  const [citation, setCitation] = useState("APA");
  const [puzzleOpts, setPuzzleOpts] = useState({ directions: "classic", audience: "Adult", trim: "8.5x11", largePrint: false });
  const pickDifficulty = (d) => {
    setDifficulty(d);
    setPuzzleOpts((o) => ({ ...o, directions: { easy: "seniors", medium: "classic", hard: "challenge" }[d] }));
  };

  useEffect(() => {
    if (initialSeriesId) {
      setSeriesId(initialSeriesId);
      setShowAdv(true);
    }
  }, [initialSeriesId]);

  const fiction = mode === "fiction";
  const children = mode === "children";
  const puzzle = ["sudoku", "maze", "coloring", "wordsearch"].includes(mode);
  const writing = mode === "journal" || mode === "workbook";
  const tones = fiction ? FICTION_TONES : children ? CHILDREN_TONES : TONES;
  const samples = fiction ? FICTION_SAMPLES : children ? CHILDREN_SAMPLES : SAMPLES;
  const maxPages = (puzzle || writing) ? 400 : children ? 24 : fiction ? 400 : 200;
  const pageStep = (puzzle || writing || children) ? 1 : 5;

  const switchMode = (m) => {
    setMode(m);
    if (["sudoku", "maze", "coloring", "wordsearch", "journal", "workbook"].includes(m)) {
      setPages((p) => (p > 400 || p < 4 ? 20 : p));
      return;
    }
    setTone(m === "fiction" ? "Literary" : m === "children" ? "Ages 5-8" : "Conversational");
    if (m === "children") setPages((p) => Math.min(p, 24) || 12);
    else setPages((p) => (p < 25 ? 60 : p));
  };

  useEffect(() => {
    if (!seed) return;
    if (seed.mode) switchMode(seed.mode);
    if (seed.topic) setTopic(seed.topic);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [seed?.n]);


  const submit = () => {
    if (!topic.trim() || busy) return;
    onGenerate({
      topic: topic.trim(), target_pages: pages, tone, include_images: images, mode,
      outline_structure: structure, pov, audience, character_bible: bible,
      chapter_words: chapterWords || undefined, difficulty,
      series_id: seriesId || undefined,
      author: author.trim() || undefined,
      author_bio: authorBio.trim() || undefined,
      reading_direction: direction,
      journal_style: journalStyle,
      workbook_style: workbookStyle,
      line_style: lineStyle,
      ...((mode === "fiction" || mode === "children") && admin ? { engine } : {}),
      ...(puzzle ? {
        audience: puzzleOpts.audience, trim: puzzleOpts.trim,
        ...(mode === "wordsearch" ? { ws_directions: puzzleOpts.directions, large_print: puzzleOpts.largePrint } : {}),
        ...(mode === "maze" ? { answer_keys_per_page: puzzleOpts.answerKeys || 4 } : {}),
      } : {}),
      ...(mode === "nonfiction" ? {
        nf_format: nfFormat,
        persona_name: persona.name || undefined,
        persona_background: persona.background || undefined,
        persona_why: persona.why || undefined,
        persona_stories: persona.stories || undefined,
        persona_voice: persona.voice || undefined,
        persona_freeform: persona.freeform || undefined,
        user_stories: userStories.trim() || undefined,
        persona_auto: personaAuto,
        tone_cinematic: toneCal.cinematic,
        tone_emotional: toneCal.emotional,
        tone_founder: toneCal.founder,
        tone_direct: toneCal.direct,
        citation_style: citation,
      } : {}),
    });
  };

  const STRUCTURES = ["Auto", "Three-Act", "Hero's Journey", "Save the Cat", "Chronological", "Nonlinear / Framed"];
  const POVS = ["Auto", "First person", "Second person", "Third person limited", "Third person omniscient", "Multiple POV", "Epistolary"];
  const AUDIENCES = ["Auto", "Children", "Young Adult", "Adult", "Academic / Professional"];
  const CHAPTER_WORDS = [[0, "Auto"], [800, "800"], [1500, "1,500"], [2500, "2,500"], [3500, "3,500"], [5000, "5,000"]];
  const advSelect = "bg-navy-950/60 border border-gold-500/12 rounded-lg px-3 py-2 font-ui text-sm text-slate-100 outline-none focus:border-gold-400 w-full";

  return (
    <div className="relative">
      {/* Hero */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
        className="text-center max-w-3xl mx-auto pt-10 pb-8"
      >
        <div className="inline-flex items-center gap-2 font-ui text-xs uppercase tracking-[0.25em] text-slate-400/90 border border-gold-400/20 bg-gold-400/5 rounded-full px-4 py-1.5 mb-6">
          <Feather className="w-3.5 h-3.5" /> Three AI minds. One finished book.
        </div>
        <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight leading-[1.05] text-slate-100">
          Type a topic.
          <br />
          <span className="text-slate-400 italic">We write the whole book.</span>
        </h1>
        <p className="font-read text-lg text-slate-400 mt-6 leading-relaxed">
          KLYR researches your subject across GPT, Claude &amp; Gemini, then compiles a
          full-length book — chapters, table of contents, page numbers and original artwork —
          ready to export as PDF or Word.
        </p>
      </motion.div>

      {/* Form card */}
      <motion.div
        initial={{ opacity: 0, y: 24 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.7, delay: 0.15, ease: [0.22, 1, 0.36, 1] }}
        className="bg-navy-900/90 border border-gold-500/12 rounded-2xl p-6 sm:p-8 shadow-2xl space-y-7 max-w-3xl mx-auto"
      >
        {/* Mode toggle */}
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 p-1.5 bg-navy-950/60 border border-gold-500/12 rounded-xl">
          <button
            data-testid="mode-nonfiction"
            onClick={() => switchMode("nonfiction")}
            className={`flex items-center justify-center gap-2 rounded-lg py-3 px-1 font-ui text-xs sm:text-sm font-medium transition-all ${
              mode === "nonfiction" ? "bg-gold-400 text-stone-950 shadow" : "text-slate-400 hover:text-slate-200"
            }`}
          >
            <BookText className="w-4 h-4 shrink-0" /> Non-fiction
          </button>
          <button
            data-testid="mode-fiction"
            onClick={() => switchMode("fiction")}
            className={`flex items-center justify-center gap-2 rounded-lg py-3 px-1 font-ui text-xs sm:text-sm font-medium transition-all ${
              mode === "fiction" ? "bg-gold-400 text-stone-950 shadow" : "text-slate-400 hover:text-slate-200"
            }`}
          >
            <Drama className="w-4 h-4 shrink-0" /> Fiction
          </button>
          <button
            data-testid="mode-children"
            onClick={() => switchMode("children")}
            className={`flex items-center justify-center gap-2 rounded-lg py-3 px-1 font-ui text-xs sm:text-sm font-medium transition-all ${
              mode === "children" ? "bg-gold-400 text-stone-950 shadow" : "text-slate-400 hover:text-slate-200"
            }`}
          >
            <Baby className="w-4 h-4 shrink-0" /> Children's
          </button>
          <button
            data-testid="mode-puzzles"
            onClick={() => switchMode("sudoku")}
            className={`flex items-center justify-center gap-2 rounded-lg py-3 px-1 font-ui text-xs sm:text-sm font-medium transition-all ${
              puzzle ? "bg-gold-400 text-stone-950 shadow" : "text-slate-400 hover:text-slate-200"
            }`}
          >
            <Puzzle className="w-4 h-4 shrink-0" /> Puzzles
          </button>
          <button
            data-testid="mode-journal"
            onClick={() => switchMode("journal")}
            className={`flex items-center justify-center gap-2 rounded-lg py-3 px-1 font-ui text-xs sm:text-sm font-medium transition-all ${
              mode === "journal" ? "bg-gold-400 text-stone-950 shadow" : "text-slate-400 hover:text-slate-200"
            }`}
          >
            <NotebookPen className="w-4 h-4 shrink-0" /> Journal
          </button>
          <button
            data-testid="mode-workbook"
            onClick={() => switchMode("workbook")}
            className={`flex items-center justify-center gap-2 rounded-lg py-3 px-1 font-ui text-xs sm:text-sm font-medium transition-all ${
              mode === "workbook" ? "bg-gold-400 text-stone-950 shadow" : "text-slate-400 hover:text-slate-200"
            }`}
          >
            <ClipboardList className="w-4 h-4 shrink-0" /> Workbook
          </button>
        </div>

        {writing && (
          <div className="space-y-4 rounded-xl border border-gold-400/30 bg-gold-400/5 p-4">
            <div>
              <label className="font-ui text-xs uppercase tracking-[0.2em] font-semibold text-slate-400/90 mb-2 block">
                {mode === "journal" ? "Journal style" : "Workbook style"}
              </label>
              <div className="grid grid-cols-2 gap-2">
                {mode === "journal" ? (
                  <>
                    <button data-testid="journal-style-guided" onClick={() => setJournalStyle("guided")}
                      className={`rounded-lg py-2.5 font-ui text-sm font-medium transition-all ${journalStyle === "guided" ? "bg-gold-400 text-stone-950" : "bg-navy-950/50 text-slate-300 border border-gold-500/12"}`}>
                      Guided prompts
                    </button>
                    <button data-testid="journal-style-plain" onClick={() => setJournalStyle("plain")}
                      className={`rounded-lg py-2.5 font-ui text-sm font-medium transition-all ${journalStyle === "plain" ? "bg-gold-400 text-stone-950" : "bg-navy-950/50 text-slate-300 border border-gold-500/12"}`}>
                      Plain pages
                    </button>
                  </>
                ) : (
                  <>
                    <button data-testid="workbook-style-educational" onClick={() => setWorkbookStyle("educational")}
                      className={`rounded-lg py-2.5 font-ui text-sm font-medium transition-all ${workbookStyle === "educational" ? "bg-gold-400 text-stone-950" : "bg-navy-950/50 text-slate-300 border border-gold-500/12"}`}>
                      Lessons + answers
                    </button>
                    <button data-testid="workbook-style-prompt" onClick={() => setWorkbookStyle("prompt")}
                      className={`rounded-lg py-2.5 font-ui text-sm font-medium transition-all ${workbookStyle === "prompt" ? "bg-gold-400 text-stone-950" : "bg-navy-950/50 text-slate-300 border border-gold-500/12"}`}>
                      Practice prompts
                    </button>
                  </>
                )}
              </div>
            </div>
            <div>
              <label className="font-ui text-xs uppercase tracking-[0.2em] font-semibold text-slate-400/90 mb-2 block">Writing lines</label>
              <div className="grid grid-cols-3 gap-2">
                {["ruled", "dot", "blank"].map((ls) => (
                  <button key={ls} data-testid={`line-style-${ls}`} onClick={() => setLineStyle(ls)}
                    className={`rounded-lg py-2 font-ui text-sm font-medium capitalize transition-all ${lineStyle === ls ? "bg-gold-400 text-stone-950" : "bg-navy-950/50 text-slate-300 border border-gold-500/12"}`}>
                    {ls === "dot" ? "Dot grid" : ls}
                  </button>
                ))}
              </div>
              <p className="font-ui text-xs text-slate-500 mt-2">
                {mode === "journal"
                  ? (journalStyle === "guided" ? "AI writes a fresh prompt on each page, with your chosen writing space below." : "Clean pages of writing space — no prompts.")
                  : (workbookStyle === "educational" ? "AI writes a mini lesson + practice questions on each page, with a full answer key at the back." : "Reflective practice prompts with blank answer space.")}
              </p>
            </div>
          </div>
        )}

        {puzzle && (
          <div className="space-y-4 rounded-xl border border-gold-400/30 bg-gold-400/5 p-4">
            <div>
              <label className="font-ui text-xs uppercase tracking-[0.2em] font-semibold text-slate-400/90 mb-2 block">Puzzle type</label>
              <div className="grid grid-cols-2 gap-2">
                <button data-testid="puzzle-type-sudoku" onClick={() => switchMode("sudoku")}
                  className={`flex items-center justify-center gap-2 rounded-lg py-2.5 font-ui text-sm font-medium transition-all ${mode === "sudoku" ? "bg-gold-400 text-stone-950" : "bg-navy-950/50 text-slate-300 border border-gold-500/12"}`}>
                  <Grid3x3 className="w-4 h-4" /> Sudoku
                </button>
                <button data-testid="puzzle-type-maze" onClick={() => switchMode("maze")}
                  className={`flex items-center justify-center gap-2 rounded-lg py-2.5 font-ui text-sm font-medium transition-all ${mode === "maze" ? "bg-gold-400 text-stone-950" : "bg-navy-950/50 text-slate-300 border border-gold-500/12"}`}>
                  <Route className="w-4 h-4" /> Maze
                </button>
                <button data-testid="puzzle-type-wordsearch" onClick={() => switchMode("wordsearch")}
                  className={`flex items-center justify-center gap-2 rounded-lg py-2.5 font-ui text-sm font-medium transition-all ${mode === "wordsearch" ? "bg-gold-400 text-stone-950" : "bg-navy-950/50 text-slate-300 border border-gold-500/12"}`}>
                  <Search className="w-4 h-4" /> Word Search
                </button>
                <button data-testid="puzzle-type-coloring" onClick={() => switchMode("coloring")}
                  className={`flex items-center justify-center gap-2 rounded-lg py-2.5 font-ui text-sm font-medium transition-all ${mode === "coloring" ? "bg-gold-400 text-stone-950" : "bg-navy-950/50 text-slate-300 border border-gold-500/12"}`}>
                  <Palette className="w-4 h-4" /> Coloring
                </button>
              </div>
              <p className="font-ui text-xs text-slate-500 mt-2">
                {mode === "coloring" ? "AI draws black-and-white line art on every page — ready to print & color."
                  : "Every book includes a full printable answer key."}
              </p>
            </div>
            {mode !== "coloring" && (
            <div>
              <label className="font-ui text-xs uppercase tracking-[0.2em] font-semibold text-slate-400/90 mb-2 block">Difficulty</label>
              <div className="grid grid-cols-3 gap-2">
                {["easy", "medium", "hard"].map((d) => (
                  <button
                    key={d}
                    data-testid={`difficulty-${d}`}
                    onClick={() => pickDifficulty(d)}
                    className={`rounded-lg py-2 font-ui text-sm font-medium capitalize transition-all ${difficulty === d ? "bg-gold-400 text-stone-950" : "bg-navy-950/50 text-slate-300 border border-gold-500/12"}`}
                  >
                    {d}
                  </button>
                ))}
              </div>
            </div>
            )}
            <PuzzleSetup mode={mode} difficulty={difficulty} value={puzzleOpts} onChange={setPuzzleOpts} />
          </div>
        )}

        <div className="space-y-3">
          <label className="font-ui text-xs uppercase tracking-[0.2em] font-semibold text-slate-400/90">
            {children ? "What's your picture book about?" : fiction ? "What's your story about?" : puzzle ? "What's the theme of your puzzle book?" : mode === "journal" ? "What's your journal about?" : mode === "workbook" ? "What's your workbook about?" : "What should your book be about?"}
          </label>
          <Textarea
            data-testid="topic-input"
            value={topic}
            onChange={(e) => setTopic(e.target.value)}
            placeholder={children
              ? "Describe your hero so we keep them consistent — e.g. Lucy, a girl with red curls and green eyes, and her puppy Bo"
              : fiction
              ? "e.g. A detective in 1920s Cairo who can hear the dead"
              : "e.g. The history and future of artificial intelligence"}
            className="min-h-[90px] resize-none bg-navy-950/60 border-gold-500/12 focus-visible:ring-gold-400/60 font-read text-lg text-slate-100 placeholder:text-slate-500"
          />
          {children && (
            <p className="font-ui text-[11px] text-emerald-400/80">
              Tip: describe your main character's look (hair, eyes, skin, clothes). We'll keep them the same on every page.
            </p>
          )}
          <div className="flex flex-wrap gap-2">
            {samples.map((s) => (
              <button
                key={s}
                data-testid={`sample-topic-${s.slice(0, 6)}`}
                onClick={() => setTopic(s)}
                className="font-ui text-xs text-slate-400 hover:text-gold-200 border border-gold-500/12 hover:border-gold-400/50 rounded-full px-3 py-1.5 transition-colors"
              >
                {s}
              </button>
            ))}
          </div>
        </div>

        {/* Author + binding direction */}
        <div className="grid sm:grid-cols-2 gap-6">
          <div className="space-y-2">
            <label className="font-ui text-xs uppercase tracking-[0.2em] font-semibold text-slate-400/90 flex items-center gap-2">
              <User className="w-4 h-4" /> Author name
            </label>
            <input
              data-testid="author-input"
              value={author}
              onChange={(e) => setAuthor(e.target.value)}
              placeholder="Your name or pen name (optional)"
              className="w-full bg-navy-950/60 border border-gold-500/12 rounded-lg px-3 py-2.5 font-ui text-sm text-slate-100 placeholder:text-slate-500 outline-none focus:border-gold-400"
            />
            <p className="font-ui text-[11px] text-slate-500">Printed on the cover and title page. You can change it later.</p>
            <textarea
              data-testid="author-bio-input"
              value={authorBio}
              maxLength={700}
              rows={3}
              onChange={(e) => setAuthorBio(e.target.value)}
              placeholder="About the author (optional), e.g. Jane Doe writes cozy mysteries from her kitchen table in Ohio."
              className="w-full bg-navy-950/60 border border-gold-500/12 rounded-lg px-3 py-2.5 font-ui text-sm text-slate-100 placeholder:text-slate-500 outline-none focus:border-gold-400 resize-y"
            />
            <p className="font-ui text-[11px] text-slate-500">Printed on the back cover exactly as you write it. Leave it empty and the back cover has no bio. We never make one up.</p>
          </div>
          <div className="space-y-2">
            <label className="font-ui text-xs uppercase tracking-[0.2em] font-semibold text-slate-400/90">
              Binding &amp; reading direction
            </label>
            <div className="grid grid-cols-2 gap-2">
              <button
                type="button"
                data-testid="direction-ltr"
                onClick={() => setDirection("ltr")}
                className={`flex items-center justify-center gap-2 rounded-lg py-2.5 font-ui text-sm font-medium transition-all ${direction === "ltr" ? "bg-gold-400 text-stone-950" : "bg-navy-950/50 text-slate-300 border border-gold-500/12 hover:text-slate-100"}`}
              >
                <AlignLeft className="w-4 h-4" /> Left-to-right
              </button>
              <button
                type="button"
                data-testid="direction-rtl"
                onClick={() => setDirection("rtl")}
                className={`flex items-center justify-center gap-2 rounded-lg py-2.5 font-ui text-sm font-medium transition-all ${direction === "rtl" ? "bg-gold-400 text-stone-950" : "bg-navy-950/50 text-slate-300 border border-gold-500/12 hover:text-slate-100"}`}
              >
                <AlignRight className="w-4 h-4" /> Right-to-left
              </button>
            </div>
            <p className="font-ui text-[11px] text-slate-500">
              {direction === "ltr" ? "Spine on the left — standard English & Western books." : "Spine on the right — manga, Arabic, Hebrew & Japanese-style binding."}
            </p>
          </div>
        </div>

        {mode === "nonfiction" && (
          <div data-testid="nf-format-group">
            <label className="font-ui text-xs uppercase tracking-[0.2em] text-slate-400 mb-2 block">Book style</label>
            <div className="grid grid-cols-2 gap-2">
              <button type="button" data-testid="nf-format-reference" onClick={() => setNfFormat("reference")}
                className={`text-left rounded-xl p-3 border transition-all ${nfFormat === "reference" ? "border-gold-400 bg-gold-400/10" : "border-gold-500/12 bg-navy-950/50 hover:border-gold-400/40"}`}>
                <div className="font-ui text-sm font-semibold text-slate-100">Reference guide</div>
                <div className="font-ui text-[11px] text-slate-400 mt-0.5">Facts, how-tos, tips & pro-tip boxes. Like a handbook or encyclopedia.</div>
              </button>
              <button type="button" data-testid="nf-format-narrative" onClick={() => setNfFormat("narrative")}
                className={`text-left rounded-xl p-3 border transition-all ${nfFormat === "narrative" ? "border-gold-400 bg-gold-400/10" : "border-gold-500/12 bg-navy-950/50 hover:border-gold-400/40"}`}>
                <div className="font-ui text-sm font-semibold text-slate-100">Narrative / self-help</div>
                <div className="font-ui text-[11px] text-slate-400 mt-0.5">Stories, case studies & frameworks that build an argument chapter by chapter.</div>
              </button>
            </div>
          </div>
        )}

        {(mode === "fiction" || mode === "children") && admin && (
          <div data-testid="engine-group" className="rounded-xl border border-indigo-500/30 bg-indigo-500/5 p-3">
            <label className="font-ui text-xs uppercase tracking-[0.2em] text-indigo-200/90 mb-2 block flex items-center gap-2">
              <Feather className="w-3.5 h-3.5" /> Generation engine <span className="text-[10px] text-slate-500 normal-case tracking-normal">· admin A/B</span>
            </label>
            <div className="grid grid-cols-2 gap-2">
              <button type="button" data-testid="engine-classic" onClick={() => setEngine("classic")}
                className={`text-left rounded-xl p-3 border transition-all ${engine === "classic" ? "border-indigo-400 bg-indigo-400/10" : "border-indigo-500/15 bg-navy-950/50 hover:border-indigo-400/40"}`}>
                <div className="font-ui text-sm font-semibold text-slate-100">Classic</div>
                <div className="font-ui text-[11px] text-slate-400 mt-0.5">Current pipeline (rolling synopsis).</div>
              </button>
              <button type="button" data-testid="engine-bible" onClick={() => setEngine("bible")}
                className={`text-left rounded-xl p-3 border transition-all ${engine === "bible" ? "border-indigo-400 bg-indigo-400/10" : "border-indigo-500/15 bg-navy-950/50 hover:border-indigo-400/40"}`}>
                <div className="font-ui text-sm font-semibold text-slate-100">Story Bible <span className="text-[10px] text-indigo-300">beta</span></div>
                <div className="font-ui text-[11px] text-slate-400 mt-0.5">Structured canon + preventative continuity.</div>
              </button>
            </div>
          </div>
        )}

        {mode === "nonfiction" && nfFormat === "narrative" && (
          <div className="rounded-xl border border-amber-500/30 bg-amber-500/5">
            <button type="button" data-testid="pro-nonfiction-toggle" onClick={() => setProOpen((o) => !o)}
              className="w-full flex items-center justify-between px-4 py-3">
              <span className="font-ui text-xs uppercase tracking-[0.2em] font-semibold text-amber-300/90 flex items-center gap-2">
                <Feather className="w-4 h-4" /> Pro non-fiction (A+ mode)
              </span>
              <span className="font-ui text-[11px] text-slate-500">{proOpen ? "Hide" : "Author persona · case studies · structure · tone"}</span>
            </button>
            {proOpen && (
              <div className="px-4 pb-5 space-y-5 border-t border-amber-500/20 pt-4">
                <p className="font-ui text-[11px] text-slate-500">
                  Every non-fiction book now auto-includes original recurring case studies, a Hook→Story→Science→Framework→Apply→Reflect chapter structure, a cinematic conclusion and a printable toolkit + 30-day plan. Add an author persona and tune the voice below.
                </p>
                <div className="flex items-center gap-2">
                  <label className="font-ui text-xs uppercase tracking-[0.15em] font-semibold text-slate-400/90 flex-1">Author persona</label>
                  <label className="flex items-center gap-2 font-ui text-xs text-slate-400 cursor-pointer">
                    <input type="checkbox" data-testid="persona-auto" checked={personaAuto} onChange={(e) => setPersonaAuto(e.target.checked)} className="accent-amber-400" />
                    Auto-draft for me
                  </label>
                </div>
                {!personaAuto && (
                  <div className="grid sm:grid-cols-2 gap-3">
                    <input data-testid="persona-name" value={persona.name} onChange={(e) => setPersona({ ...persona, name: e.target.value })} placeholder="Author name / who they are"
                      className="bg-navy-950/60 border border-gold-500/12 rounded-lg px-3 py-2.5 font-ui text-sm text-slate-100 placeholder:text-slate-500 outline-none focus:border-amber-500/60" />
                    <input data-testid="persona-background" value={persona.background} onChange={(e) => setPersona({ ...persona, background: e.target.value })} placeholder="Background & credibility"
                      className="bg-navy-950/60 border border-gold-500/12 rounded-lg px-3 py-2.5 font-ui text-sm text-slate-100 placeholder:text-slate-500 outline-none focus:border-amber-500/60" />
                    <input data-testid="persona-why" value={persona.why} onChange={(e) => setPersona({ ...persona, why: e.target.value })} placeholder="Why they wrote this book"
                      className="bg-navy-950/60 border border-gold-500/12 rounded-lg px-3 py-2.5 font-ui text-sm text-slate-100 placeholder:text-slate-500 outline-none focus:border-amber-500/60" />
                    <input data-testid="persona-voice" value={persona.voice} onChange={(e) => setPersona({ ...persona, voice: e.target.value })} placeholder="Voice & personality"
                      className="bg-navy-950/60 border border-gold-500/12 rounded-lg px-3 py-2.5 font-ui text-sm text-slate-100 placeholder:text-slate-500 outline-none focus:border-amber-500/60" />
                    <textarea data-testid="persona-stories" value={persona.stories} onChange={(e) => setPersona({ ...persona, stories: e.target.value })} placeholder="Signature personal stories to thread in (optional)" rows={2}
                      className="sm:col-span-2 bg-navy-950/60 border border-gold-500/12 rounded-lg px-3 py-2.5 font-ui text-sm text-slate-100 placeholder:text-slate-500 outline-none focus:border-amber-500/60 resize-none" />
                    <textarea data-testid="persona-freeform" value={persona.freeform} onChange={(e) => setPersona({ ...persona, freeform: e.target.value })} placeholder="Free-text override — anything else about the author's voice (optional)" rows={2}
                      className="sm:col-span-2 bg-navy-950/60 border border-gold-500/12 rounded-lg px-3 py-2.5 font-ui text-sm text-slate-100 placeholder:text-slate-500 outline-none focus:border-amber-500/60 resize-none" />
                    <div className="sm:col-span-2">
                      <label className="block font-ui text-xs font-semibold text-slate-300 mb-1">Your own stories <span className="text-slate-500 font-normal">(optional)</span></label>
                      <textarea data-testid="user-stories" value={userStories} onChange={(e) => setUserStories(e.target.value)} placeholder="Paste 1-3 real anecdotes from your life, a client or a friend (including a moment of failure). The AI weaves each into the chapter where it fits best, in your voice." rows={3}
                        className="w-full bg-navy-950/60 border border-gold-500/12 rounded-lg px-3 py-2.5 font-ui text-sm text-slate-100 placeholder:text-slate-500 outline-none focus:border-amber-500/60 resize-none" />
                    </div>
                  </div>
                )}
                <div>
                  <label className="font-ui text-xs uppercase tracking-[0.15em] font-semibold text-slate-400/90 mb-3 block">Tone calibration</label>
                  <div className="space-y-4">
                    {[["cinematic", "Academic", "Cinematic"], ["emotional", "Analytical", "Emotional"], ["founder", "Researcher", "Founder"], ["direct", "Soft", "Direct"]].map(([k, lo, hi]) => (
                      <div key={k}>
                        <div className="flex justify-between font-ui text-[11px] text-slate-500 mb-1"><span>{lo}</span><span>{hi}</span></div>
                        <input type="range" min={0} max={100} step={5} value={toneCal[k]} data-testid={`tone-${k}`}
                          onChange={(e) => setToneCal({ ...toneCal, [k]: Number(e.target.value) })}
                          className="w-full accent-amber-400" />
                      </div>
                    ))}
                  </div>
                </div>
                <div>
                  <label className="font-ui text-xs uppercase tracking-[0.15em] font-semibold text-slate-400/90 mb-2 block">Citations & references</label>
                  <div className="flex flex-wrap gap-2">
                    {["APA", "MLA", "Chicago", "None"].map((cs) => (
                      <button key={cs} type="button" data-testid={`citation-${cs}`} onClick={() => setCitation(cs)}
                        className={`rounded-lg px-4 py-2 font-ui text-sm font-medium transition-all ${citation === cs ? "bg-gold-400 text-stone-950" : "bg-navy-950/50 text-slate-300 border border-gold-500/12"}`}>
                        {cs === "None" ? "No references" : cs}
                      </button>
                    ))}
                  </div>
                  <p className="font-ui text-[11px] text-slate-500 mt-2">Adds a crisp SVG framework diagram + a references section built from real, well-known sources (verify before publishing).</p>
                </div>
              </div>
            )}
          </div>
        )}

        <div className="grid sm:grid-cols-2 gap-6">
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <label className="font-ui text-xs uppercase tracking-[0.2em] font-semibold text-slate-400/90 flex items-center gap-2">
                <Layers className="w-4 h-4" /> {puzzle ? "Puzzles" : "Length"}
              </label>
              <span className="font-display text-2xl font-bold text-slate-100">
                {pages}<span className="text-sm font-ui font-normal text-slate-500 ml-1">{puzzle ? "puzzles" : "pages"}</span>
              </span>
            </div>
            <Slider
              data-testid="page-count-slider"
              value={[pages]}
              min={puzzle || writing || children ? 4 : 5}
              max={maxPages}
              step={pageStep}
              onValueChange={(v) => setPages(v[0])}
              className="[&_[role=slider]]:bg-gold-400 [&_[role=slider]]:border-gold-400 [&>span:first-child>span]:bg-gold-400"
            />
            <div className="flex justify-between font-ui text-[10px] text-slate-500">
              <span>{puzzle ? "Few · 4" : children ? "Short · 4" : writing ? "Few · 4" : "Pamphlet · 5"}</span>
              <span>{puzzle ? "Big · 400" : children ? "Big · 24" : fiction ? "Epic · 400" : "Epic · 200"}</span>
            </div>
            <p className="font-ui text-[11px] text-slate-500">
              {puzzle
                ? `${pages} ${mode} puzzles, each with a printable answer key at the back.`
                : writing
                ? `${pages} printable ${mode} pages with ${lineStyle === "dot" ? "dot-grid" : lineStyle} writing space.`
                : children
                ? "Each page has its own illustration of your character."
                : `~${(pages * 275).toLocaleString()} words. Longer books take more time to compile.`}
            </p>
          </div>

          <div className="space-y-4">
            {!puzzle && !writing && (
              <>
                <label className="font-ui text-xs uppercase tracking-[0.2em] font-semibold text-slate-400/90">
                  {children ? "Age & style" : fiction ? "Genre" : "Writing tone"}
                </label>
                <Select value={tone} onValueChange={setTone}>
                  <SelectTrigger
                    data-testid="tone-select"
                    className="bg-navy-950/60 border-gold-500/12 font-ui text-slate-100 focus:ring-gold-400/60"
                  >
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-navy-900 border-gold-500/12 text-slate-100">
                    {tones.map((t) => (
                      <SelectItem key={t} value={t} data-testid={`tone-option-${t}`}>
                        {t}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </>
            )}

            <div className="flex items-center justify-between bg-navy-950/60 border border-gold-500/12 rounded-lg px-4 py-3">
              <div className="flex items-center gap-2 font-ui text-sm text-slate-200">
                <ImageIcon className="w-4 h-4 text-slate-400" /> {(puzzle || writing) ? "AI cover art" : "AI illustrations"}
              </div>
              <Switch
                data-testid="include-images-switch"
                checked={images}
                onCheckedChange={setImages}
                className="data-[state=checked]:bg-gold-400"
              />
            </div>
          </div>
        </div>

        {/* Advanced (Character Bible, structure, POV, audience, length) */}
        {!puzzle && !writing && (
        <div className="border-t border-gold-500/12 pt-4">
          <button
            data-testid="advanced-toggle"
            onClick={() => setShowAdv((s) => !s)}
            className="flex items-center gap-2 font-ui text-xs uppercase tracking-[0.2em] font-semibold text-slate-400/90"
          >
            <Feather className="w-3.5 h-3.5" /> {showAdv ? "Hide" : "Show"} advanced controls
          </button>
          {showAdv && (
            <div className="mt-5 space-y-5">
              <div className="space-y-2">
                <label className="font-ui text-xs uppercase tracking-[0.2em] font-semibold text-slate-400/90">
                  Character Bible <span className="text-slate-500 normal-case tracking-normal">— reused on every {children ? "page" : "chapter"}</span>
                </label>
                <Textarea
                  data-testid="character-bible-input"
                  value={bible}
                  onChange={(e) => setBible(e.target.value)}
                  placeholder="Protagonists, relationships, world rules, themes, tone, chapter beats to honor… kept consistent throughout."
                  className="min-h-[90px] resize-none bg-navy-950/60 border-gold-500/12 focus-visible:ring-gold-400/60 font-read text-slate-100 placeholder:text-slate-500"
                />
              </div>

              {/* Series Mode */}
              <div className="space-y-2">
                <label className="font-ui text-xs uppercase tracking-[0.2em] font-semibold text-slate-400/90">
                  Series <span className="text-slate-500 normal-case tracking-normal">— reuse characters &amp; world across books</span>
                </label>
                <div className="flex flex-wrap items-center gap-3">
                  <select
                    data-testid="series-select"
                    value={seriesId}
                    onChange={(e) => {
                      const v = e.target.value;
                      const s = seriesList.find((x) => x.id === v);
                      if (s && s.status !== "active") { onBuySeries && onBuySeries(s); return; }
                      setSeriesId(v);
                    }}
                    className={advSelect + " sm:max-w-xs"}
                  >
                    <option value="">Standalone book (no series)</option>
                    {seriesList.map((s) => (
                      <option key={s.id} value={s.id}>
                        {s.name}{s.status !== "active" ? " — locked ($19)" : ""}
                      </option>
                    ))}
                  </select>
                  <button
                    type="button"
                    data-testid="new-series-button"
                    onClick={() => onCreateSeries && onCreateSeries()}
                    className="font-ui text-sm text-slate-300 border border-gold-400/40 hover:border-gold-300 rounded-lg px-4 py-2 transition-colors"
                  >
                    + New series
                  </button>
                  <button
                    type="button"
                    data-testid="manage-series-button"
                    onClick={() => onManageSeries && onManageSeries()}
                    className="font-ui text-sm text-slate-400 hover:text-gold-200 transition-colors"
                  >
                    Manage series →
                  </button>
                </div>
              </div>
              <div className="grid sm:grid-cols-3 gap-4">
                {!children && (
                  <div className="space-y-1.5">
                    <label className="font-ui text-[11px] uppercase tracking-wider text-slate-400">Outline structure</label>
                    <select data-testid="structure-select" value={structure} onChange={(e) => setStructure(e.target.value)} className={advSelect}>
                      {STRUCTURES.map((s) => <option key={s} value={s}>{s}</option>)}
                    </select>
                  </div>
                )}
                {!children && (
                  <div className="space-y-1.5">
                    <label className="font-ui text-[11px] uppercase tracking-wider text-slate-400">Point of view</label>
                    <select data-testid="pov-select" value={pov} onChange={(e) => setPov(e.target.value)} className={advSelect}>
                      {POVS.map((p) => <option key={p} value={p}>{p}</option>)}
                    </select>
                  </div>
                )}
                <div className="space-y-1.5">
                  <label className="font-ui text-[11px] uppercase tracking-wider text-slate-400">Audience band</label>
                  <select data-testid="audience-select" value={audience} onChange={(e) => setAudience(e.target.value)} className={advSelect}>
                    {AUDIENCES.map((a) => <option key={a} value={a}>{a}</option>)}
                  </select>
                </div>
                {!children && (
                  <div className="space-y-1.5">
                    <label className="font-ui text-[11px] uppercase tracking-wider text-slate-400">Words / chapter</label>
                    <select data-testid="chapter-words-select" value={chapterWords} onChange={(e) => setChapterWords(Number(e.target.value))} className={advSelect}>
                      {CHAPTER_WORDS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                    </select>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
        )}

        <button
          data-testid="generate-book-button"
          onClick={submit}
          disabled={!topic.trim() || busy}
          className="group w-full flex items-center justify-center gap-3 bg-gold-400 hover:bg-gold-300 disabled:opacity-40 disabled:cursor-not-allowed text-stone-950 font-ui font-semibold text-lg rounded-xl px-6 py-4 transition-all hover:shadow-[0_0_40px_-8px_rgba(127,156,180,0.7)]"
        >
          <Wand2 className="w-5 h-5 group-hover:rotate-12 transition-transform" />
          {busy ? "Summoning the AI writers…" : entitled ? (children ? "Make my picture book" : fiction ? "Write my story" : puzzle ? "Build my puzzle book" : mode === "journal" ? "Build my journal" : mode === "workbook" ? "Build my workbook" : "Write my book") : "Create free preview"}
        </button>
        <p className="text-center font-ui text-xs text-slate-500">
          {!entitled
            ? "Free preview = cover + table of contents + first page. Unlock the full book with a credit or subscription."
            : puzzle
            ? `A ${pages}-puzzle ${mode} book with a full answer key compiles in under a minute.`
            : writing
            ? `A ${pages}-page ${mode} ${mode === "workbook" && workbookStyle === "educational" ? "with a full answer key " : ""}compiles in about a minute.`
            : children
            ? `A ${pages}-page picture book with matching art takes a few minutes to compile.`
            : `A ${pages}-page ${fiction ? "novel" : "book"} takes a few minutes to compile${pages > 80 ? " — large books can take 10+ minutes." : "."}`}
        </p>
      </motion.div>
    </div>
  );
};
